import Badge from "@/components/base/Badge";
import type { School, PlatformStat } from "@/types";

interface EnrollmentComparisonProps {
  schools: School[];
  stats: PlatformStat[];
}

interface Row {
  school: School;
  stat: PlatformStat;
}

export default function EnrollmentComparison({ schools, stats }: EnrollmentComparisonProps) {
  const rows: Row[] = schools
    .map((school) => ({ school, stat: stats.find((s) => s.schoolId === school.id) }))
    .filter((r): r is Row => Boolean(r.stat))
    .sort((a, b) => b.stat.students - a.stat.students);

  const maxStudents = Math.max(1, ...rows.map((r) => r.stat.students));
  const maxTeachers = Math.max(1, ...rows.map((r) => r.stat.teachers));

  return (
    <div className="space-y-3">
      {rows.map(({ school, stat }, index) => {
        const width = Math.max(4, Math.round((stat.students / maxStudents) * 100));
        const ratio = stat.teachers > 0 ? Math.round(stat.students / stat.teachers) : 0;
        const girlsShare = stat.students > 0 ? Math.round((stat.girls / stat.students) * 100) : 0;
        return (
          <div
            key={school.id}
            className="rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-background-300"
          >
            <div className="flex items-center gap-3">
              <span
                className={`w-7 h-7 shrink-0 rounded-md flex items-center justify-center text-xs font-semibold ${
                  index === 0 ? "bg-accent-100 text-accent-800" : "bg-background-100 text-foreground-700"
                }`}
              >
                {index + 1}
              </span>
              <span className="w-9 h-9 shrink-0 overflow-hidden rounded-md border border-background-200 bg-background-100 flex items-center justify-center">
                {school.logo ? (
                  <img
                    src={school.logo}
                    alt={school.name}
                    title={school.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <i className="ri-building-2-line text-foreground-500"></i>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold font-heading text-foreground-950">{school.shortName}</p>
                <p className="truncate text-xs text-foreground-500">{school.city}</p>
              </div>
              <div className="text-right">
                <p className="text-base font-semibold font-heading text-foreground-950">
                  {stat.students.toLocaleString("fr-FR")}
                </p>
                <p className="text-xs uppercase tracking-wide text-foreground-400">élèves</p>
              </div>
            </div>

            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-background-100">
              <div className="h-full rounded-full bg-primary-600" style={{ width: `${width}%` }}></div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-foreground-600">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-women-line text-secondary-600"></i>
                Filles : <strong className="font-medium text-foreground-900">{stat.girls.toLocaleString("fr-FR")}</strong>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-men-line text-secondary-600"></i>
                Garçons :{" "}
                <strong className="font-medium text-foreground-900">{stat.boys.toLocaleString("fr-FR")}</strong>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-star-line text-primary-600"></i>
                Enseignants :{" "}
                <strong className="font-medium text-foreground-900">{stat.teachers.toLocaleString("fr-FR")}</strong>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-group-line text-foreground-400"></i>
                Ratio : <strong className="font-medium text-foreground-900">{ratio}:1</strong>
              </span>
              <Badge tone="secondary" className="ml-auto">
                {girlsShare}% de filles
              </Badge>
            </div>
          </div>
        );
      })}
      <p className="pt-1 text-xs text-foreground-500">
        Les barres sont proportionnelles au plus grand effectif
        {" "}
        <strong className="font-medium text-foreground-700">({maxStudents.toLocaleString("fr-FR")} élèves)</strong>. Plus
        grand nombre d'enseignants : {maxTeachers}.
      </p>
    </div>
  );
}